import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import {
    UpdateCartLineApiParameter,
    updateCartLineWithResult as updateCartLineApi,
} from "@insite/client-framework/Services/CartService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { addToCartEvent, removeFromCartEvent } from "@WhiteCap/Common/Utillities/CustomTracking";

interface UpdateCartLineParameter {
    onSuccess?: () => void;
    cartLine: CartLineModel;
    shouldNotReloadPromotions?: boolean;
    shouldNotLoadFullCart?: boolean;
    previousQty?: number | null;
}

type HandlerType = ApiHandlerDiscreteParameter<UpdateCartLineParameter, UpdateCartLineApiParameter>;

export const DispatchBeginUpdateCartLine: HandlerType = props => {
    props.dispatch({
        type: "Context/BeginUpdatingCart",
    });
    props.dispatch({
        type: "Pages/Cart/BeginUpdateCartLine",
    });
};

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = {
        ...props.parameter,
        cartId: API_URL_CURRENT_FRAGMENT,
    };
};

export const UpdateCartLine: HandlerType = async props => {
    await updateCartLineApi(props.apiParameter);
};

export const TriggerUpdateQtyEvent: HandlerType = props => {
    const session = getSession(props.getState());
    if (props.parameter.cartLine?.qtyOrdered && props.parameter.previousQty) {
        const unitPrice = props.parameter.cartLine?.pricing?.unitNetPrice ?? 0;
        const itemName = props.parameter.cartLine?.shortDescription;
        const itemId = props.parameter.cartLine?.erpNumber;
        const brand = props.parameter.cartLine?.brand?.name || "";

        const qtyChange =
            props.parameter.cartLine.qtyOrdered - props.parameter.previousQty;

        // Common item data
        const itemData = {
            item_name: itemName,
            item_id: itemId,
            quantity: Math.abs(qtyChange),
            price: unitPrice,
            item_brand: brand,
        };

        if (qtyChange > 0) {
            // Items added
            addToCartEvent({
                event: "add_to_cart",
                ecommerce: {
                    currency: session?.currency?.currencyCode,
                    value: unitPrice * qtyChange, // price × qty added
                    items: [itemData],
                },
            });
        } else if (qtyChange < 0) {
            // Items removed
            removeFromCartEvent({
                event: "remove_from_cart",
                ecommerce: {
                    currency: session?.currency?.currencyCode,
                    value: unitPrice * Math.abs(qtyChange), // price × qty removed
                    items: [itemData],
                },
            });
        }
    }
};


export const LoadCart: HandlerType = props => {
    props.parameter.shouldNotLoadFullCart
        ? props.dispatch(
            loadCurrentCart({
                shouldLoadFullCart: false,
                getCartLines: true,
                getCostCodes: true,
                getPromotions: !props.parameter.shouldNotReloadPromotions,
            }),
        )
        : props.dispatch(loadCurrentCart({ getPromotions: !props.parameter.shouldNotReloadPromotions }));
};

/** @deprecated Not needed anymore */
export const LoadPromotions: HandlerType = () => { };

export const DispatchCompleteUpdateCartLine: HandlerType = props => {
    props.dispatch({
        type: "Pages/Cart/CompleteUpdateCartLine",
    });
    props.dispatch({
        type: "Context/CompleteUpdatingCart",
    });
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.();
};

export const chain = [
    DispatchBeginUpdateCartLine,
    PopulateApiParameter,
    UpdateCartLine,
    TriggerUpdateQtyEvent,
    LoadCart,
    LoadPromotions,
    DispatchCompleteUpdateCartLine,
    ExecuteOnSuccessCallback,
];

const updateCartLineExpanded = createHandlerChainRunner(chain, "UpdateCartLine");
export default updateCartLineExpanded;